import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService, { authFetch } from '../services/api';
import '../styles/QuestionsManagement.css';

interface AnswerOption {
  text: string;
  score: number;
}

interface QuestionData {
  id?: string;
  component: string;
  attribute: string;
  question: string;
  weight?: number;
  attributeId?: string;
  answerOptions?: AnswerOption[];
}

const QuestionsManagement: React.FC = () => {
  const { hasPermission, currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [questions, setQuestions] = useState<QuestionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  
  // Filter states
  const [componentFilter, setComponentFilter] = useState<string>('');
  const [attributeFilter, setAttributeFilter] = useState<string>('');
  const [editingQuestion, setEditingQuestion] = useState<QuestionData | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<QuestionData>({
    component: '',
    attribute: '',
    question: '',
    weight: 3
  });
  const [csvPreview, setCsvPreview] = useState<QuestionData[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [viewMode, setViewMode] = useState(false);

  const canModify = hasPermission('canManageSettings');
  const isAdmin = currentUser?.role === 'Administrator';

  // Load existing questions
  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const components = await apiService.getComponents();
      const allQuestions: QuestionData[] = [];

      components.forEach((comp: any) => {
        comp.attributes?.forEach((attr: any) => {
          if (attr.question) {
            allQuestions.push({
              id: attr.question.id,
              component: comp.name,
              attribute: attr.name,
              question: attr.question.text,
              weight: attr.weight,
              attributeId: attr.id
            });
          }
        });
      });

      setQuestions(allQuestions);
    } catch (error) {
      console.error('Error loading questions:', error);
      setUploadStatus('Failed to load existing questions');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setUploadStatus('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  // Robust CSV line parser that handles quoted fields, commas, and special characters
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (inQuotes) {
        if (char === '"') {
          if (nextChar === '"') {
            // Escaped quote ("") - add single quote and skip next
            current += '"';
            i += 2;
            continue;
          } else {
            // End of quoted field
            inQuotes = false;
            i++;
            continue;
          }
        } else {
          // Regular character inside quotes
          current += char;
          i++;
          continue;
        }
      } else {
        if (char === '"') {
          // Start of quoted field
          inQuotes = true;
          i++;
          continue;
        } else if (char === ',') {
          // Field delimiter - normalize special characters and add to result
          result.push(normalizeSpecialChars(current.trim()));
          current = '';
          i++;
          continue;
        } else {
          // Regular character outside quotes
          current += char;
          i++;
          continue;
        }
      }
    }

    // Don't forget the last field
    result.push(normalizeSpecialChars(current.trim()));

    return result;
  };

  // Normalize special characters like different types of dashes, quotes, etc.
  const normalizeSpecialChars = (text: string): string => {
    if (!text) return text;

    return text
      // Normalize various dash types to standard hyphen
      .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
      // Normalize various quote types to standard quotes
      .replace(/[\u2018\u2019\u201A\u201B]/g, "'")  // Single quotes
      .replace(/[\u201C\u201D\u201E\u201F]/g, '"')  // Double quotes
      // Normalize whitespace around dashes (e.g., " - " stays as " - ")
      .replace(/\s*-\s*/g, (match) => match.includes(' ') ? ' - ' : '-')
      // Normalize multiple spaces to single space
      .replace(/\s+/g, ' ')
      // Remove any null/invisible characters (eslint warning is intentional - we need to match control chars)
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .trim();
  };

  const parseCSV = async (text: string) => {
    // Normalize line endings and split
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedText.split('\n').filter(line => line.trim());

    // Parse headers using robust parser
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());

    // Validate headers
    const requiredHeaders = ['component', 'attribute', 'question'];
    const hasAllHeaders = requiredHeaders.every(h =>
      headers.some(header => header.includes(h.slice(0, 3)))
    );

    if (!hasAllHeaders) {
      setUploadStatus('CSV must have columns: Component, Attribute, Question');
      setValidationErrors(['Invalid CSV format: Missing required columns']);
      return;
    }

    const parsedData: QuestionData[] = [];

    for (let i = 1; i < lines.length; i++) {
      // Use robust CSV parser for each line
      const values = parseCSVLine(lines[i]);

      if (values.length >= 3 && values[0] && values[1] && values[2]) {
        // Parse answer options from columns: Answer1, Score1, Answer2, Score2, etc.
        // Columns: 0=Component, 1=Attribute, 2=Question, 3=Answer1, 4=Score1, 5=Answer2, 6=Score2...
        // Note: Weight is NOT in the CSV - it will be looked up from existing attributes by the API
        const answerOptions: AnswerOption[] = [];

        // Parse up to 5 answer/score pairs starting at index 3
        for (let j = 0; j < 5; j++) {
          const answerIndex = 3 + (j * 2);  // 3, 5, 7, 9, 11
          const scoreIndex = 4 + (j * 2);   // 4, 6, 8, 10, 12

          const answerText = values[answerIndex]?.trim();
          const scoreValue = parseInt(values[scoreIndex]) || (j + 1);

          if (answerText) {
            answerOptions.push({ text: answerText, score: scoreValue });
          }
        }

        parsedData.push({
          component: values[0],
          attribute: values[1],
          question: values[2],
          // Weight is omitted - API will lookup from existing attribute or use default
          answerOptions: answerOptions.length > 0 ? answerOptions : undefined
        });
      }
    }

    if (parsedData.length === 0) {
      setUploadStatus('No valid data found in CSV');
      setValidationErrors(['No valid question rows found in CSV file']);
      return;
    }

    // Validate against existing data structure (informational only - backend will create missing items)
    try {
      const components = await apiService.getComponents();
      const warnings: string[] = [];
      let newComponentsCount = 0;
      let newAttributesCount = 0;
      let updateCount = 0;

      for (let i = 0; i < parsedData.length; i++) {
        const row = parsedData[i];

        // Check component exists
        const component = components.find((c: any) =>
          c.name.toLowerCase() === row.component.toLowerCase()
        );

        if (!component) {
          // Component will be created by backend - just count it
          newComponentsCount++;
          newAttributesCount++; // Attribute will also be new
          continue;
        }

        // Check attribute exists in component
        const attribute = component.attributes?.find((a: any) =>
          a.name.toLowerCase() === row.attribute.toLowerCase()
        );

        if (!attribute) {
          // Attribute will be created by backend - just count it
          newAttributesCount++;
          continue;
        }

        // Check if question already exists (will be updated)
        if (attribute.question) {
          updateCount++;
        }
      }

      // No blocking errors - backend handles creation of missing components/attributes
      setValidationErrors(warnings);

      const newQuestionsCount = parsedData.length - updateCount;

      let statusMessage = `Found ${parsedData.length} questions to import.`;
      if (newComponentsCount > 0) {
        statusMessage += ` Will create ${newComponentsCount} new component(s).`;
      }
      if (newAttributesCount > 0) {
        statusMessage += ` Will create ${newAttributesCount} new attribute(s).`;
      }
      statusMessage += ` (${newQuestionsCount} new questions, ${updateCount} updates)`;

      setUploadStatus(statusMessage);

    } catch (error) {
      console.error('Validation error:', error);
      setValidationErrors(['Failed to validate data against existing framework']);
      setUploadStatus('Failed to validate CSV data');
      return;
    }

    setCsvPreview(parsedData);
    setShowPreview(true);
  };

  const handleConfirmImport = async () => {
    if (!canModify) {
      setUploadStatus('You do not have permission to import questions');
      return;
    }

    setLoading(true);
    setUploadStatus('Processing import...');
    
    try {
      const results = await apiService.importQuestionsFromCSV(csvPreview);

      if (results.errors.length > 0) {
        console.error('Import errors:', results.errors);

        // Show detailed error information
        const errorDetails = results.errors.join('\n');
        alert(`Import completed with errors:\n\n${errorDetails}\n\nSuccessfully imported: ${results.imported} questions\nFailed: ${results.errors.length} questions`);

        setUploadStatus(`Imported ${results.imported} questions with ${results.errors.length} error(s). Check the alert for details.`);
      } else {
        setUploadStatus(`Successfully imported ${results.imported} questions.`);
      }

      // Reload questions
      await loadQuestions();

      // Clear preview
      setCsvPreview([]);
      setShowPreview(false);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error importing questions:', error);
      setUploadStatus('Failed to import questions. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = () => {
    setCsvPreview([]);
    setShowPreview(false);
    setUploadStatus(null);
    setValidationErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'Component',
      'Attribute',
      'Question',
      'Answer Option 1',
      'Score 1',
      'Answer Option 2',
      'Score 2',
      'Answer Option 3',
      'Score 3',
      'Answer Option 4',
      'Score 4',
      'Answer Option 5',
      'Score 5'
    ];

    const sampleRows = [
      'Alignment to Global Standards,Compliance with industry frameworks,How well does the application comply with industry-standard frameworks and regulations?,Fully compliant with all frameworks,5,Mostly compliant with minor gaps,4,Partially compliant,3,Limited compliance,2,Non-compliant or unknown,1',
      'Alignment to Strategy,Strategic relevance,How strategically relevant is this application to the organization\'s long-term goals?,Critical to strategic goals,5,Highly relevant,4,Moderately relevant,3,Low relevance,2,Not relevant,1',
      'Infrastructure Costs,Hosting model (on-prem/cloud),What is the current hosting model (on-premise private cloud public cloud hybrid)?,Public cloud - lowest cost,5,Hybrid cloud - balanced,4,Private cloud,3,On-premise optimized,2,On-premise legacy,1',
      'Operational Costs,Support team size,How large is the support team required to maintain this application?,Minimal support needed (1-2 people),5,Small team (3-5 people),4,Medium team (6-10 people),3,Large team (11-20 people),2,Very large team (20+ people),1'
    ];

    const template = headers.join(',') + '\n' + sampleRows.join('\n');

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_with_answers_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadQuestionsData = async () => {
    if (questions.length === 0) {
      alert('No questions data to download');
      return;
    }

    setLoading(true);
    setUploadStatus('Preparing download with answer options...');

    try {
      const headers = [
        'Component',
        'Attribute',
        'Question',
        'Answer Option 1',
        'Score 1',
        'Answer Option 2',
        'Score 2',
        'Answer Option 3',
        'Score 3',
        'Answer Option 4',
        'Score 4',
        'Answer Option 5',
        'Score 5'
      ];
      const csvRows = [headers.join(',')];

      // Fetch answer options for each question
      for (const q of questions) {
        const row = [
          `"${(q.component || '').replace(/"/g, '""')}"`,
          `"${(q.attribute || '').replace(/"/g, '""')}"`,
          `"${(q.question || '').replace(/"/g, '""')}"`
        ];

        // Fetch answer options from API if question has an ID
        let answers: AnswerOption[] = [];
        if (q.id) {
          try {
            const response = await authFetch(`/api/questions/${q.id}/answer-options`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
              }
            });
            if (response.ok) {
              answers = await response.json();
            }
          } catch (err) {
            console.error(`Failed to fetch answer options for question ${q.id}:`, err);
          }
        }

        // Add answer options (up to 5)
        for (let i = 0; i < 5; i++) {
          if (answers[i]) {
            row.push(`"${(answers[i].text || '').replace(/"/g, '""')}"`);
            row.push(String(answers[i].score || (i + 1)));
          } else {
            row.push('""');
            row.push('');
          }
        }
        csvRows.push(row.join(','));
      }

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions_data_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setUploadStatus('Download complete!');
      setTimeout(() => setUploadStatus(null), 3000);
    } catch (error) {
      console.error('Error downloading questions data:', error);
      setUploadStatus('Failed to download questions data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setFormData({
      component: '',
      attribute: '',
      question: '',
      weight: 3
    });
    setEditingQuestion(null);
    setViewMode(false);
    setShowAddForm(true);
  };

  const handleViewQuestion = (question: QuestionData) => {
    setFormData(question);
    setEditingQuestion(question);
    setViewMode(true);
    setShowAddForm(true);
  };

  const handleEditQuestion = (question: QuestionData) => {
    setFormData(question);
    setEditingQuestion(question);
    setViewMode(false);
    setShowAddForm(true);
  };

  const handleDeleteQuestion = async (question: QuestionData) => {
    if (!canModify || !question.attributeId) return;
    
    const confirmed = window.confirm(`Delete question for "${question.attribute}"?`);
    if (!confirmed) return;

    try {
      await apiService.deleteAttribute(question.attributeId);
      await loadQuestions();
      setUploadStatus(`Deleted question for ${question.attribute}`);
    } catch (error) {
      console.error('Error deleting question:', error);
      setUploadStatus('Failed to delete question');
    }
  };

  const handleSaveQuestion = async () => {
    if (!canModify) return;
    
    try {
      if (editingQuestion && editingQuestion.attributeId) {
        // Update existing
        await apiService.updateAttribute(editingQuestion.attributeId, {
          name: formData.attribute,
          weight: formData.weight || 3,
          description: `${formData.component} - ${formData.attribute}`
        });
        setUploadStatus('Question updated successfully');
      } else {
        // Create new - this would need backend support for creating full hierarchy
        setUploadStatus('Creating individual questions requires dimension and component to exist');
      }
      
      await loadQuestions();
      setShowAddForm(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error('Error saving question:', error);
      setUploadStatus('Failed to save question');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'weight' ? parseInt(value) || 3 : value
    }));
  };

  const handleDeleteAllData = async () => {
    if (!isAdmin) {
      setUploadStatus('Only administrators can delete all questions');
      return;
    }

    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm deletion');
      return;
    }

    setLoading(true);
    setUploadStatus('Deleting all questions...');

    try {
      // Delete all questions (not attributes, just the questions)
      const components = await apiService.getComponents();
      let deletedCount = 0;

      for (const comp of components) {
        for (const attr of comp.attributes || []) {
          if (attr.question && attr.question.id) {
            try {
              await apiService.deleteQuestion(attr.question.id);
              deletedCount++;
            } catch (error) {
              console.error(`Failed to delete question ${attr.question.id} for attribute ${attr.id}:`, error);
            }
          }
        }
      }

      setUploadStatus(`Successfully deleted ${deletedCount} questions`);
      await loadQuestions();
      setShowDeleteAllModal(false);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting all questions:', error);
      setUploadStatus('Failed to delete all questions. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteAllModal(false);
    setDeleteConfirmText('');
  };

  return (
    <div className="page questions-management">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Questions Management</h1>
          </div>
          <div className="page-header__actions">
            {canModify && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={handleAddQuestion}
                  disabled={loading}
                >
                  ➕ Add Question
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={downloadTemplate}
                  title="Download CSV template for bulk questions upload"
                >
                  📋 Download Template
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload CSV file with questions"
                >
                  📤 Upload Questions
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </>
            )}
            {isAdmin && questions.length > 0 && (
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteAllModal(true)}
                disabled={loading}
              >
                🗑️ Delete All Data
              </button>
            )}
            {questions.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadQuestionsData}
                title="Download current questions data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters - Fixed section with higher z-index to stay above sticky table header */}
      <div className="md-card" style={{ marginBottom: '16px', padding: '16px', position: 'relative', zIndex: 20, backgroundColor: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {/* Component Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '80px' }}>Component:</label>
            <input
              type="text"
              placeholder="Search components..."
              value={componentFilter}
              onChange={(e) => setComponentFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                width: '220px'
              }}
            />
            {componentFilter && (
              <button
                onClick={() => setComponentFilter('')}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Attribute Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '80px' }}>Attribute:</label>
            <input
              type="text"
              placeholder="Search attributes..."
              value={attributeFilter}
              onChange={(e) => setAttributeFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                width: '220px'
              }}
            />
            {attributeFilter && (
              <button
                onClick={() => setAttributeFilter('')}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Filter Results Counter and Clear All */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {(componentFilter || attributeFilter) ?
                `Showing ${questions.filter(q =>
                  (!componentFilter || q.component.toLowerCase().includes(componentFilter.toLowerCase())) &&
                  (!attributeFilter || q.attribute.toLowerCase().includes(attributeFilter.toLowerCase()))
                ).length} of ${questions.length} questions` :
                `${questions.length} questions`
              }
            </span>
            <button
              onClick={() => {
                setComponentFilter('');
                setAttributeFilter('');
              }}
              style={{
                padding: '4px 8px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Upload Status Message - only shown when there's a status */}
      {uploadStatus && (
        <div className="upload-section">
          <div className={`status-message ${uploadStatus.includes('error') || uploadStatus.includes('Failed') ? 'error' : 'success'}`}>
            {uploadStatus}
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="form-modal">
          <div className="form-modal-content" style={{ maxWidth: '500px' }}>
            <div className="form-modal-header" style={{ backgroundColor: '#dc3545' }}>
              <h2>⚠️ Confirm Delete All Questions</h2>
              <button
                className="btn-close"
                onClick={handleCancelDeleteAll}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, color: '#856404', fontWeight: '600', fontSize: '14px' }}>
                  ⚠️ WARNING: This action cannot be undone!
                </p>
                <p style={{ margin: '8px 0 0 0', color: '#856404', fontSize: '13px' }}>
                  This will permanently delete all {questions.length} questions from the system.
                </p>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  Type <strong style={{ color: '#dc3545' }}>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #dc3545',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                  autoFocus
                />
              </div>

              <div className="form-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelDeleteAll}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteAllData}
                  disabled={loading || deleteConfirmText !== 'DELETE'}
                  style={{
                    opacity: deleteConfirmText !== 'DELETE' ? 0.5 : 1,
                    cursor: deleteConfirmText !== 'DELETE' ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Deleting...' : '🗑️ Delete All Questions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Preview */}
      {showPreview && csvPreview.length > 0 && (
        <div className="csv-preview">
          <h3>Preview Import Data ({csvPreview.length} questions)</h3>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div style={{
              backgroundColor: '#fee',
              border: '1px solid #c33',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <h4 style={{ color: '#c33', marginTop: 0, marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                ⚠️ Validation Errors ({validationErrors.length})
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#c33' }}>
                {validationErrors.slice(0, 10).map((error, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{error}</li>
                ))}
              </ul>
              {validationErrors.length > 10 && (
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#c33', fontStyle: 'italic' }}>
                  ... and {validationErrors.length - 10} more errors
                </p>
              )}
            </div>
          )}

          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Attribute</th>
                  <th>Question</th>
                </tr>
              </thead>
              <tbody>
                {csvPreview.slice(0, 10).map((row, index) => (
                  <tr key={index}>
                    <td>{row.component}</td>
                    <td>{row.attribute}</td>
                    <td>{row.question}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {csvPreview.length > 10 && (
              <p className="preview-note">Showing first 10 of {csvPreview.length} questions</p>
            )}
          </div>
          <div className="preview-actions" style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-success"
              onClick={handleConfirmImport}
              disabled={loading || validationErrors.length > 0}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                minWidth: '180px',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                justifyContent: 'center',
                opacity: (loading || validationErrors.length > 0) ? 0.5 : 1,
                cursor: (loading || validationErrors.length > 0) ? 'not-allowed' : 'pointer',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s'
              }}
              title={validationErrors.length > 0 ? 'Cannot import: Fix validation errors first' : 'Import questions'}
            >
              <span>✅</span>
              <span>Confirm Import</span>
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCancelImport}
              disabled={loading}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                minWidth: '120px',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                justifyContent: 'center',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s'
              }}
            >
              <span>❌</span>
              <span>Cancel</span>
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '500px',
              maxWidth: '600px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                {viewMode ? 'View Question' : (editingQuestion ? 'Edit Question' : 'Add New Question')}
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {viewMode
                  ? 'View question details (read-only)'
                  : (editingQuestion
                    ? 'Update question information'
                    : 'Add a new question to the APR framework')}
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveQuestion(); }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="component" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Component *
                </label>
                <input
                  type="text"
                  id="component"
                  name="component"
                  value={formData.component}
                  onChange={handleInputChange}
                  required
                  disabled={viewMode || editingQuestion !== null}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: (viewMode || editingQuestion !== null) ? '#f5f5f5' : 'white',
                    cursor: (viewMode || editingQuestion !== null) ? 'not-allowed' : 'text'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="attribute" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Attribute *
                </label>
                <input
                  type="text"
                  id="attribute"
                  name="attribute"
                  value={formData.attribute}
                  onChange={handleInputChange}
                  required
                  disabled={viewMode}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: viewMode ? '#f5f5f5' : 'white',
                    cursor: viewMode ? 'not-allowed' : 'text'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="question" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Question *
                </label>
                <textarea
                  id="question"
                  name="question"
                  value={formData.question}
                  onChange={handleInputChange}
                  rows={3}
                  required
                  disabled={viewMode}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: viewMode ? '#f5f5f5' : 'white',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    cursor: viewMode ? 'not-allowed' : 'text'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="weight" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Attribute Weight (1-5)
                </label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  min="1"
                  max="5"
                  value={formData.weight}
                  onChange={handleInputChange}
                  disabled={viewMode}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: viewMode ? '#f5f5f5' : 'white',
                    cursor: viewMode ? 'not-allowed' : 'text'
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#666',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {viewMode ? 'Close' : 'Cancel'}
                </button>
                {!viewMode && (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Save
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Existing Questions Table */}
      <div className="questions-table-section">
        <h2>Existing Questions ({questions.filter(q =>
          (!componentFilter || q.component.toLowerCase().includes(componentFilter.toLowerCase())) &&
          (!attributeFilter || q.attribute.toLowerCase().includes(attributeFilter.toLowerCase()))
        ).length})</h2>
        {loading ? (
          <p>Loading...</p>
        ) : questions.length === 0 ? (
          <p className="no-data">No questions found. Import from CSV or add manually.</p>
        ) : (
          <div className="table-container">
            <table className="questions-table">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Attribute</th>
                  <th>Attribute Weight</th>
                  <th>Question</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.filter(q =>
                  (!componentFilter || q.component.toLowerCase().includes(componentFilter.toLowerCase())) &&
                  (!attributeFilter || q.attribute.toLowerCase().includes(attributeFilter.toLowerCase()))
                ).map((q, index) => (
                  <tr key={q.id || index}>
                    <td>{q.component}</td>
                    <td>{q.attribute}</td>
                    <td>
                      <span className={`weight weight-${q.weight}`}>
                        {q.weight}
                      </span>
                    </td>
                    <td>{q.question}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewQuestion(q)}
                          title="View question details"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          👁️
                        </button>
                        {canModify && (
                          <>
                            <button
                              className="action-btn edit-btn"
                              onClick={() => handleEditQuestion(q)}
                              title="Edit question"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteQuestion(q)}
                              title="Delete question"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#d32f2f',
                                border: '1px solid #d32f2f',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default QuestionsManagement;